import { useSelectedApPayee } from '@/components/ApPayee/useChangeApPayee';
import { useSelectedApPayeeIsBankTransfer } from '@/components/ApPayee/useChangeIsBankTransfer';
import { InputSuffix } from '@/components/InputSuffix/InputSuffix';
import { useIsActiveInstantPayee } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useIsActiveInstantPayee';
import { TableTextOverflow } from '@/components/TableTextOverflow/TableTextOverflow';
import { useTpmDataFromBusinessPersonData } from '@/context/BusinessPersonData';
import { InvoiceKind } from '@/context/services/invoice_kind/InvoiceKind.service';
import { INVOICE_NUMBER_UNSAVED } from '@/context/services/reportsType/invoiceReports/type';
import { CrItemsSelectCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/CrItemsSelectCell';
import { CrSubItemsSelectCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/CrSubItemsSelectCell';
import { CurrencySelectCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/CurrenciesSelectCell';
import { DealDate } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/DealDate';
import { DeptSelectCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/DeptSelectCell';
import { ExItemSelectCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/ExItemSelectCell';
import { ExciseSelectCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/ExciseSelectCell';
import { FormInputCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/FormInputCell';
import { FormInputNumberCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/FormInputNumberCell';
import { ProjectSelectCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/ProjectSelectCell';
import { RateInputCell } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/RateInputCell';
import { useUpdateForCurrencyAndRate } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/hooks/useUpdateForCurrencyAndRate';
import { useUpdateRateValue } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/hooks/useUpdateRateValue';
import { getForeignCurrencyDisableProps } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/utils/getForeignCurrencyDisableProps';
import { CustomTransactionItems } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Components/CustomTransactionItems';
import { useInvoiceReportNavigation } from '@/features/InvoiceReport/Edit/components/hooks/useGetInvoiceReportPath';
import { useVisibleTransactionProperties } from '@/features/InvoiceReport/Edit/components/hooks/useVisibleTransactionProperties';
import type { InvoiceTransactionErrorType } from '@/features/InvoiceReport/Edit/components/type';
import {
  DEFAULT_FIELDS,
  TAX_RELATED_DEFAULT_FIELDS,
} from '@/features/InvoiceReport/constants';
import { useFlexibleValidationFlag } from '@/hooks/feature_flag/useFlexibleValidationFlag';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  FormController,
  InputNumberProps,
  Loading,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import { FORMATS } from '@moneyforward/ap-frontend-components/dist/es/atoms/Form/Input/InputNumber';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useCallback, useEffect, useMemo } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import type {
  InvoiceTransaction,
  PaymentRequestForm,
  TransactionProperty,
} from '../../../type';
import { Actions } from './Actions/Actions';
import { ExciseValueCell } from './Cells/ExciseValueCell';
import { HasWithHoldingIncomeTaxCheckBox } from './Cells/HasWithHoldingIncomeTaxCheckBox';
import { InvoiceKind as InvoiceKindCell } from './Cells/InvoiceKind';
import { RequiredHeader } from './Cells/RequiredHeader';
import { TransactionError } from './Cells/TransactionError';
import {
  useInvoiceTransactionHelper,
  useInvoiceTransactionSupporters,
  useValidationRules,
} from './hooks';

const MIN_COLUMN_WIDTH = 80;

export type UseColumnsOption = {
  isTaxIncluded: boolean;
  canDisplayTransactionDetail: boolean;
  canDisplayDrExcise: boolean;
  isPermitMinusTransaction: boolean;
  hasWithholdingIncomeTax: boolean;
  hasSpecialException: boolean;
  isDutyFree: boolean;
  invoiceTransactionRowErrors: InvoiceTransactionErrorType | undefined;
};

type Column = TableProps<InvoiceTransaction>['columns'][number];
export type Columns = Column[];

type GetCurrencySuffixPropsFn = (currency: string | undefined) => object;
type GetWithholdingTaxInputPropsFn = (
  hasWithholdingIncomeTax: boolean | undefined,
  foreignCurrencyDisable: boolean
) => object;

export type ComputeInputPropsFn =
  | GetCurrencySuffixPropsFn
  | GetWithholdingTaxInputPropsFn;

export type ComputeInputPropsFnParams =
  | [string | undefined]
  | [boolean | undefined, boolean];

export const useColumns = (
  control: Control<PaymentRequestForm, unknown>,
  fields: InvoiceTransaction[],
  option: UseColumnsOption | undefined = undefined
): Columns => {
  const {
    isTaxIncluded,
    canDisplayTransactionDetail,
    canDisplayDrExcise,
    isPermitMinusTransaction,
    hasWithholdingIncomeTax,
    hasSpecialException,
    isDutyFree,
    invoiceTransactionRowErrors,
  } = useMemo(
    () =>
      option ?? {
        isTaxIncluded: false,
        canDisplayTransactionDetail: true,
        canDisplayDrExcise: true,
        isPermitMinusTransaction: false,
        hasWithholdingIncomeTax: false,
        hasSpecialException: false,
        isDutyFree: false,
        invoiceTransactionRowErrors: undefined,
      },
    [option]
  );

  const { isApproverRoleFromPath } = useInvoiceReportNavigation();
  const { t } = useTranslation();
  const containerRef = useGlobalContainerRef();
  const selectedPayee = useSelectedApPayee();

  const isInvoiceKindUnqualifiedSeventyFlag = useBooleanFlagValue(
    'invoice-kind-unqualified-seventy',
    false
  );

  const isSupportGlobalRequiredSetting = useFlexibleValidationFlag();

  const visibleTransactionProperties = useVisibleTransactionProperties({
    canDisplayTransactionDetail,
    canDisplayDrExcise,
    control,
  });

  const {
    dealDateRule,
    nameRule,
    exItemIdRule,
    quantityRule,
    unitRule,
    taxIncludedUnitRule,
    taxIncludedTotalValueRule,
    totalValueRule,
    deptIdRule,
    projectIdRule,
    memoRule,
    hasWithholdingIncomeTaxRule,
    withholdingIncomeTaxRule,
    crItemIdRule,
    currencyRule,
    customTransactionItemsRule,
  } = useValidationRules(
    isTaxIncluded,
    isPermitMinusTransaction,
    canDisplayTransactionDetail,
    hasWithholdingIncomeTax,
    undefined,
    isSupportGlobalRequiredSetting,
    visibleTransactionProperties
  );
  const tpmData = useTpmDataFromBusinessPersonData();
  const helpers = useInvoiceTransactionHelper(
    isTaxIncluded,
    canDisplayTransactionDetail,
    canDisplayDrExcise,
    hasSpecialException,
    isDutyFree,
    tpmData
  );
  const { getIsZeroPer } = helpers;
  const {
    dealDateSupporter,
    drExciseIdSupporter,
    exItemIdSupporter,
    exciseValueSupporter,
    quantitySupporter,
    totalValueSupporter,
    unitSupporter,
    withholdingIncomeTaxSupporter,
    hasWithholdingIncomeTaxSupporter,
    currencyValueSupporter,
  } = useInvoiceTransactionSupporters(control, helpers);

  const unitFormName: { label: string; name: 'taxIncludedUnit' | 'unit' } =
    useMemo(
      () => ({
        name: isTaxIncluded ? 'taxIncludedUnit' : 'unit',
        label: isTaxIncluded
          ? t('invoice_transaction_unit_tax_include')
          : t('invoice_transaction_unit'),
      }),
      [isTaxIncluded, t]
    );
  const totalValueFormName: {
    name: 'taxIncludedTotalValue' | 'totalValue';
    label: string;
  } = useMemo(
    () => ({
      name: isTaxIncluded ? 'taxIncludedTotalValue' : 'totalValue',
      label: isTaxIncluded
        ? t('invoice_transaction_total_value_tax_include')
        : t('invoice_transaction_total_value'),
    }),
    [isTaxIncluded, t]
  );

  const rateInputProps = useMemo(
    () => ({ format: 'unit' } satisfies InputNumberProps),
    []
  );
  const baseSelectProps = useMemo(
    () => ({ getPopupContainer: containerRef }),
    [containerRef]
  );
  const getIndex = useCallback(
    (row: InvoiceTransaction) =>
      fields.findIndex((field) => field.rowKey === row.rowKey),
    [fields]
  );

  const { triggerRateValueSupporter, loading } = useUpdateRateValue({
    getIndex,
    selectedPayee,
  });

  const isActiveInstantPayee = useIsActiveInstantPayee({
    control,
    name: 'payee.value',
  });
  const isBankTransfer = useSelectedApPayeeIsBankTransfer();
  const { getValues, setValue, getFieldState } =
    useFormContext<PaymentRequestForm>();
  const updateTransactionsWithWithholdingTax = useCallback(
    (transactions: InvoiceTransaction[], hasWithholdingTax: boolean) => {
      return transactions.map((transaction) => ({
        ...transaction,
        hasWithholdingIncomeTax: hasWithholdingTax
          ? transaction.hasWithholdingIncomeTax
          : false,
        withholdingIncomeTax: hasWithholdingTax
          ? transaction.withholdingIncomeTax
          : 0,
      }));
    },
    []
  );

  // hasWithholdingIncomeTax and withholdingIncomeTax is always updated
  const handleDirtyPayee = useCallback(() => {
    const invoiceTransactions = getValues('invoiceTransactions');
    invoiceTransactions.forEach((_, idx) =>
      hasWithholdingIncomeTaxSupporter(
        idx,
        selectedPayee?.is_withholding_tax || false
      )
    );
  }, [
    getValues,
    hasWithholdingIncomeTaxSupporter,
    selectedPayee?.is_withholding_tax,
  ]);

  // If hasWithholdingIncomeTax is not change, withholdingIncomeTax is the same as the previous value
  // If the selectedPayee?.is_withholding_tax is false, withholdingIncomeTax is 0
  const handleCleanPayee = useCallback(() => {
    const invoiceTransactions: InvoiceTransaction[] = getValues(
      'invoiceTransactions'
    );
    const hasWithholdingTax = selectedPayee?.is_withholding_tax || false;
    const updatedTransactions = updateTransactionsWithWithholdingTax(
      invoiceTransactions,
      hasWithholdingTax
    );
    setValue('invoiceTransactions', updatedTransactions);
  }, [
    getValues,
    selectedPayee?.is_withholding_tax,
    setValue,
    updateTransactionsWithWithholdingTax,
  ]);

  useEffect(() => {
    const { isDirty } = getFieldState('payee.value');
    if (isDirty) {
      handleDirtyPayee();
    } else {
      handleCleanPayee();
    }
  }, [getFieldState, handleDirtyPayee, handleCleanPayee]);
  useUpdateForCurrencyAndRate(getValues, setValue);

  const memorizedCurrencyArgs = useMemo(() => {
    const cache = new Map<string, [string | undefined]>();
    const emptyArgs: [string | undefined] = [undefined];
    return (r: InvoiceTransaction) => {
      const key = r?.currency;
      if (!key) return emptyArgs;
      if (!cache.has(key)) cache.set(key, [r?.currency]);
      return cache.get(key)!;
    };
  }, []);

  const memorizedWithholdingTaxArgs = useMemo(() => {
    const cache = new Map<string, [boolean | undefined, boolean]>();
    return (a: boolean | undefined, b: boolean) => {
      const key = `${a}-${b}`;
      if (!cache.has(key)) cache.set(key, [a, b]);
      return cache.get(key)!;
    };
  }, []);

  const emptyInputProps = useMemo(() => ({}), []);

  const getCurrencySuffixProps = useCallback(
    (currency: string | undefined) => {
      if (!currency || currency === 'JPY') return emptyInputProps;
      return { suffix: <InputSuffix suffix={currency} /> };
    },
    [emptyInputProps]
  );

  const getCurrencySelectProps = useCallback(
    (currency: string | undefined) => {
      return {
        ...baseSelectProps,
        disabled: isBankTransfer || isActiveInstantPayee,
        value:
          isBankTransfer || isActiveInstantPayee ? 'JPY' : currency ?? 'JPY',
      };
    },
    [baseSelectProps, isBankTransfer, isActiveInstantPayee]
  );

  const getRateInputProps = useCallback(
    (jpyRate: number | undefined) => {
      return {
        ...rateInputProps,
        disabled: isBankTransfer || isActiveInstantPayee,
        value: isBankTransfer || isActiveInstantPayee ? 1 : jpyRate ?? 1,
      };
    },
    [rateInputProps, isBankTransfer, isActiveInstantPayee]
  );

  const getWithholdingTaxInputProps = useCallback(
    (
      hasWithholdingIncomeTax: boolean | undefined,
      foreignCurrencyDisable: boolean
    ) => {
      return {
        disabled: !hasWithholdingIncomeTax || foreignCurrencyDisable,
      };
    },
    []
  );

  const renderDefaultFieldFunctions = useMemo(
    (): Record<
      string,
      (v: string, r: InvoiceTransaction) => React.ReactNode
    > => ({
      [DEFAULT_FIELDS.DEAL_DATE]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return (
          <DealDate<PaymentRequestForm>
            row={r}
            index={index}
            control={control}
            inputId={`dealDate${index}`}
            name={`invoiceTransactions.${index}.dealDate`}
            rules={dealDateRule(index)}
            onChangeAfter={() => {
              triggerRateValueSupporter(r);
              dealDateSupporter(index, hasSpecialException, tpmData);
            }}
            containerRef={containerRef}
          />
        );
      },
      [DEFAULT_FIELDS.NAME]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return (
          <FormInputCell<PaymentRequestForm>
            control={control}
            inputId={`name${index}`}
            name={`invoiceTransactions.${index}.name`}
            rules={nameRule(index)}
            row={r}
            index={index}
          />
        );
      },
      [DEFAULT_FIELDS.DEPT]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const deptDefaultOption =
          r.deptId && r.deptName
            ? [
                {
                  label: r.deptName ?? '',
                  value: r.deptId ?? '',
                },
              ]
            : undefined;
        return (
          <DeptSelectCell<PaymentRequestForm>
            control={control}
            inputId={`deptId${index}`}
            name={`invoiceTransactions.${index}.deptId`}
            rules={deptIdRule(index)}
            selectProps={baseSelectProps}
            defaultOptions={deptDefaultOption}
            index={index}
            row={r}
          />
        );
      },
      [DEFAULT_FIELDS.EX_ITEM]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const exItemDefaultOption =
          r.exItemId && r.exItemName
            ? [
                {
                  value: r.exItemId ?? '',
                  label: r.exItemName ?? '',
                },
              ]
            : undefined;
        return (
          <ExItemSelectCell<PaymentRequestForm>
            key={`${r.id}_${v}_${index}`}
            control={control}
            inputId={`exItem${index}`}
            name={`invoiceTransactions.${index}.exItemId`}
            rules={exItemIdRule(index)}
            deptId={r.deptId}
            onChangeAfter={() =>
              exItemIdSupporter(index, hasSpecialException, tpmData)
            }
            selectProps={baseSelectProps}
            defaultOptions={exItemDefaultOption}
            drExciseIdName={`invoiceTransactions.${index}.drExciseId`}
            deptIdName={`invoiceTransactions.${index}.deptId`}
            row={r}
            index={index}
          />
        );
      },
      [DEFAULT_FIELDS.QUANTITY]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return (
          <FormInputNumberCell<PaymentRequestForm>
            control={control}
            row={r}
            index={index}
            inputId={`quantity${index}`}
            name={`invoiceTransactions.${index}.quantity`}
            rules={quantityRule(index)}
            onChangeAfter={() => quantitySupporter(index)}
          />
        );
      },
      [DEFAULT_FIELDS.UNIT]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return (
          <FormInputNumberCell<PaymentRequestForm>
            key={`${r.id}_${index}_unit_${isTaxIncluded}}`}
            control={control}
            row={r}
            index={index}
            inputId={`unit${index}`}
            rules={isTaxIncluded ? taxIncludedUnitRule(index) : unitRule(index)}
            computeInputProps={getCurrencySuffixProps}
            computeInputPropsParams={memorizedCurrencyArgs(r)}
            format={FORMATS.UNIT_WITH_EMPTY}
            name={`invoiceTransactions.${index}.${unitFormName.name}`}
            onChangeAfter={() => unitSupporter(index)}
          />
        );
      },
      [DEFAULT_FIELDS.AMOUNT]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return (
          <FormInputNumberCell<PaymentRequestForm>
            key={`${r.id}_${index}_totalValue_${isTaxIncluded}`}
            control={control}
            row={r}
            index={index}
            inputId={`totalValue${index}`}
            rules={
              isTaxIncluded
                ? taxIncludedTotalValueRule(index)
                : totalValueRule(index)
            }
            computeInputProps={getCurrencySuffixProps}
            computeInputPropsParams={memorizedCurrencyArgs(r)}
            format={FORMATS.UNIT_WITH_EMPTY}
            name={`invoiceTransactions.${index}.${totalValueFormName.name}`}
            onChangeAfter={() => totalValueSupporter(index)}
          />
        );
      },
      [DEFAULT_FIELDS.TAX_CATEGORY]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const drExciseDefaultOption =
          r.drExciseId && r.drExciseName
            ? [
                {
                  value: r.drExciseId ?? '',
                  label: r.drExciseName ?? '',
                },
              ]
            : undefined;
        return (
          <ExciseSelectCell<PaymentRequestForm>
            control={control}
            inputId={`drExciseId${index}`}
            name={`invoiceTransactions.${index}.drExciseId`}
            onChangeAfter={() => {
              drExciseIdSupporter(
                index,
                undefined,
                hasSpecialException,
                tpmData
              );
            }}
            selectProps={baseSelectProps}
            defaultOptions={drExciseDefaultOption}
            index={index}
            row={r}
          />
        );
      },
      [DEFAULT_FIELDS.INVOICE_KIND]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const isNotTarget = r.invoiceKind === InvoiceKind.not_target;
        return isApproverRoleFromPath ? (
          <InvoiceKindCell
            key={`${r.id}_${v}_${index}`}
            control={control}
            inputId={`invoiceKind${index}`}
            name={`invoiceTransactions.${index}.invoiceKind`}
            isNotTarget={
              isInvoiceKindUnqualifiedSeventyFlag
                ? isNotTarget
                : getIsZeroPer(r.drExciseId)
            }
            selectProps={baseSelectProps}
          />
        ) : (
          <FormController
            control={control}
            name={`invoiceTransactions.${index}.invoiceKind`}
          >
            {(...rest) => <input {...rest} hidden />}
          </FormController>
        );
      },
      [DEFAULT_FIELDS.TAX_AMOUNT]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return (
          <ExciseValueCell<PaymentRequestForm>
            key={`${r.id}_${r.drExciseId}_${r[unitFormName.name]}_${
              r[totalValueFormName.name]
            }_${r.currency}_${index}_${r.exItemId}`}
            control={control}
            row={r}
            index={index}
            inputId={`exciseValue${index}`}
            name={`invoiceTransactions.${index}.exciseValue`}
            currency={r.currency}
            drExciseId={r.drExciseId}
            onChangeAfter={() => exciseValueSupporter(index)}
            format={FORMATS.UNIT_WITH_EMPTY}
          />
        );
      },
      [DEFAULT_FIELDS.WITHHOLDING_TAX_CHECK]: (
        v: string,
        r: InvoiceTransaction
      ) => {
        const index = getIndex(r);
        const { disabled } = getForeignCurrencyDisableProps({
          paymentMethod: selectedPayee?.payment_method,
          currency: r?.currency,
        });
        return (
          <HasWithHoldingIncomeTaxCheckBox
            onChangeAfter={() => withholdingIncomeTaxSupporter(index)}
            key={`${r.id}_${v}_${index}_${r.hasWithholdingIncomeTax}`}
            control={control}
            inputId={`hasWithholdingIncomeTax${index}`}
            name={`invoiceTransactions.${index}.hasWithholdingIncomeTax`}
            label={t('invoice_transaction_withholding_income_tax_label')}
            rules={hasWithholdingIncomeTaxRule(index)}
            disabled={disabled}
          />
        );
      },
      [DEFAULT_FIELDS.WITHHOLDING_TAX_AMOUNT]: (
        v: string,
        r: InvoiceTransaction
      ) => {
        const index = getIndex(r);
        const invoiceTransactions = getValues('invoiceTransactions');
        const updatedTransaction = invoiceTransactions[index];
        const { disabled: foreignCurrencyDisable } =
          getForeignCurrencyDisableProps({
            paymentMethod: selectedPayee?.payment_method,
            currency: r?.currency,
          });
        return (
          <FormInputNumberCell<PaymentRequestForm>
            key={`${r.id}_${r.exItemId}_${r.totalValue}_${r.taxIncludedTotalValue}_${r.hasWithholdingIncomeTax}_${r.currency}_${index}`}
            control={control}
            row={r}
            index={index}
            inputId={`withholdingIncomeTax${index}`}
            name={`invoiceTransactions.${index}.withholdingIncomeTax`}
            rules={withholdingIncomeTaxRule(index)}
            format={FORMATS.UNIT_WITH_EMPTY}
            computeInputProps={getWithholdingTaxInputProps}
            computeInputPropsParams={memorizedWithholdingTaxArgs(
              updatedTransaction?.hasWithholdingIncomeTax,
              foreignCurrencyDisable
            )}
          />
        );
      },
      [DEFAULT_FIELDS.MEMO]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return (
          <FormInputCell<PaymentRequestForm>
            control={control}
            inputId={`memo${index}`}
            name={`invoiceTransactions.${index}.memo`}
            rules={memoRule(index)}
            row={r}
            index={index}
          />
        );
      },
      [DEFAULT_FIELDS.PROJECT]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const projectId = r.projectId;
        const projectName = r.projectName;
        const projectDefaultOption =
          projectId && projectName
            ? [
                {
                  value: projectId ?? '',
                  label: projectName ?? '',
                },
              ]
            : undefined;
        return (
          <ProjectSelectCell<PaymentRequestForm>
            control={control}
            inputId={`projectId${index}`}
            name={`invoiceTransactions.${index}.projectId`}
            rules={projectIdRule(index)}
            selectProps={baseSelectProps}
            defaultOptions={projectDefaultOption}
            index={index}
            row={r}
          />
        );
      },
      [DEFAULT_FIELDS.CR_ITEM]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const crItemDefaultOption =
          r.crItemId && r.crItemName
            ? [
                {
                  value: r.crItemId ?? '',
                  label: r.crItemName ?? '',
                },
              ]
            : undefined;
        return (
          <CrItemsSelectCell<PaymentRequestForm>
            control={control}
            inputId={`crItemId${index}`}
            name={`invoiceTransactions.${index}.crItemId`}
            rules={crItemIdRule(index)}
            selectProps={baseSelectProps}
            defaultOptions={crItemDefaultOption}
            index={index}
            row={r}
          />
        );
      },
      [DEFAULT_FIELDS.CR_SUB_ITEM]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const crSubItemDefaultOption =
          r.crSubItemId && r.crSubItemName
            ? [
                {
                  value: r.crSubItemId ?? '',
                  label: r.crSubItemName ?? '',
                },
              ]
            : undefined;
        return (
          <CrSubItemsSelectCell<PaymentRequestForm>
            key={`cr_sub_items_${r.rowKey}`}
            control={control}
            inputId={`crSubItemId${index}`}
            name={`invoiceTransactions.${index}.crSubItemId`}
            crItemName={`invoiceTransactions.${index}.crItemId`}
            selectProps={baseSelectProps}
            defaultOptions={crSubItemDefaultOption}
            index={index}
            row={r}
          />
        );
      },
      [DEFAULT_FIELDS.CURRENCY]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        const currencyDefaultOption = r.currency
          ? [{ value: r.currency ?? '', label: r.currency ?? '' }]
          : undefined;
        return (
          <CurrencySelectCell<PaymentRequestForm>
            key={`currency_${r.rowKey}`}
            control={control}
            inputId={`currency${index}`}
            name={`invoiceTransactions.${index}.currency`}
            rules={currencyRule(index)}
            selectProps={getCurrencySelectProps(r?.currency)}
            defaultOptions={currencyDefaultOption}
            onChangeAfter={() => {
              triggerRateValueSupporter(r);
              currencyValueSupporter(index);
            }}
            index={index}
            row={r}
          />
        );
      },
      [DEFAULT_FIELDS.JPY_RATE]: (v: string, r: InvoiceTransaction) => {
        const index = getIndex(r);
        return loading[index] ? (
          <Loading size='small' />
        ) : (
          <RateInputCell<PaymentRequestForm>
            control={control}
            row={r}
            index={index}
            inputId={`jpyRate${index}`}
            name={`invoiceTransactions.${index}.jpyRate`}
            inputProps={getRateInputProps(r?.jpyRate)}
          />
        );
      },
    }),
    [
      getIndex,
      control,
      dealDateRule,
      containerRef,
      triggerRateValueSupporter,
      dealDateSupporter,
      hasSpecialException,
      tpmData,
      nameRule,
      deptIdRule,
      baseSelectProps,
      exItemIdRule,
      exItemIdSupporter,
      quantityRule,
      quantitySupporter,
      memorizedCurrencyArgs,
      isTaxIncluded,
      taxIncludedUnitRule,
      unitRule,
      getCurrencySuffixProps,
      unitFormName.name,
      unitSupporter,
      taxIncludedTotalValueRule,
      totalValueRule,
      totalValueFormName.name,
      totalValueSupporter,
      drExciseIdSupporter,
      isApproverRoleFromPath,
      getIsZeroPer,
      isInvoiceKindUnqualifiedSeventyFlag,
      exciseValueSupporter,
      selectedPayee?.payment_method,
      t,
      hasWithholdingIncomeTaxRule,
      withholdingIncomeTaxSupporter,
      getValues,
      withholdingIncomeTaxRule,
      getWithholdingTaxInputProps,
      memorizedWithholdingTaxArgs,
      memoRule,
      projectIdRule,
      crItemIdRule,
      currencyRule,
      getCurrencySelectProps,
      currencyValueSupporter,
      loading,
      getRateInputProps,
    ]
  );

  const renderCustomizableField = useCallback(
    (property: TransactionProperty, index: number) => {
      const Component = CustomTransactionItems[property.componentType];

      const component = Component ? (
        <Component
          index={index}
          property={property}
          control={control}
          inputId={`customTransactionItem${index}`}
          name={`invoiceTransactions.${index}.custom_transaction_items.${property.id}`}
          rules={customTransactionItemsRule(index, property)}
        />
      ) : null;
      return component;
    },
    [control, customTransactionItemsRule]
  );

  const getDefaultFieldLabel = useCallback(
    (masterFieldName: string) => {
      const label = t(`invoice_transaction.column.${masterFieldName}`);

      if (TAX_RELATED_DEFAULT_FIELDS.includes(masterFieldName)) {
        return (
          label +
          (isTaxIncluded
            ? t(`invoice_transaction.column.tax_include`)
            : t(`invoice_transaction.column.tax_exclude`))
        );
      }

      return label;
    },
    [t, isTaxIncluded]
  );

  const dynamicColumns = useMemo(() => {
    return visibleTransactionProperties.flatMap(
      (property: TransactionProperty, index: number) => {
        const label =
          property.type === 'custom' ? (
            <TableTextOverflow
              title={property.label}
              tooltipMessage={property.tooltipMessage}
              isLastColumn={index === visibleTransactionProperties.length - 1}
              isTextAlignEnd={property.componentType === 'numeric'}
            />
          ) : (
            getDefaultFieldLabel(property.masterFieldName)
          );
        const columnConfig: Column = {
          title: property.isRequired ? (
            <RequiredHeader>{label}</RequiredHeader>
          ) : (
            label
          ),
          dataIndex: property.label,
          render(v: string, r: InvoiceTransaction, index: number) {
            if (property.type === 'custom') {
              return renderCustomizableField(property, index) ?? null;
            }
            const renderFn =
              renderDefaultFieldFunctions[property.masterFieldName];
            return renderFn ? renderFn(v, r) : null;
          },
          resizable: true,
          width: property.columnWidth,
          resizableMinWidth: Math.min(property.columnWidth, MIN_COLUMN_WIDTH),
        };
        return columnConfig;
      }
    );
  }, [
    visibleTransactionProperties,
    getDefaultFieldLabel,
    renderDefaultFieldFunctions,
    renderCustomizableField,
  ]);

  const fixedLeftColumns = useMemo(
    () => [
      {
        title: t('invoice_transaction_number'),
        dataIndex: 'number',
        onCell: (r: InvoiceTransaction) => ({
          className:
            invoiceTransactionRowErrors?.index === getIndex(r)
              ? 'error-cell'
              : '',
        }),
        render(value: string, r: InvoiceTransaction) {
          const index = getIndex(r);
          const errors = invoiceTransactionRowErrors?.index === index;
          if (errors) {
            return (
              <TransactionError
                messages={invoiceTransactionRowErrors?.messages}
              />
            );
          }
          return value === INVOICE_NUMBER_UNSAVED ? '' : value;
        },
        width: 80,
        fixed: 'left',
      } satisfies Column,
      {
        title: t('invoice_transaction_action'),
        render(v, r: InvoiceTransaction) {
          const index = getIndex(r);
          return <Actions index={index} />;
        },
        width: 44,
        fixed: 'left',
      } satisfies Column,
    ],
    [t, invoiceTransactionRowErrors, getIndex]
  );

  const defaultDataColumns = useMemo(
    () =>
      [
        {
          title: (
            <RequiredHeader>
              {t('invoice_transaction_deal_date')}
            </RequiredHeader>
          ),
          dataIndex: 'dealDate',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            return (
              <DealDate<PaymentRequestForm>
                row={r}
                index={index}
                control={control}
                inputId={`dealDate${index}`}
                name={`invoiceTransactions.${index}.dealDate`}
                rules={dealDateRule(index)}
                onChangeAfter={() => {
                  triggerRateValueSupporter(r);
                  dealDateSupporter(index, hasSpecialException, tpmData);
                }}
                containerRef={containerRef}
              />
            );
          },
          resizable: true,
          width: 150,
        } satisfies Column,
        {
          title: (
            <RequiredHeader>{t('invoice_transaction_name')}</RequiredHeader>
          ),
          dataIndex: 'name',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            return (
              <FormInputCell<PaymentRequestForm>
                control={control}
                inputId={`name${index}`}
                name={`invoiceTransactions.${index}.name`}
                rules={nameRule(index)}
                row={r}
                index={index}
              />
            );
          },
          resizable: true,
          width: 220,
        } satisfies Column,
        {
          title: t('invoice_transaction_department'),
          dataIndex: 'deptId',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            const defaultOption =
              r.deptId && r.deptName
                ? [
                    {
                      label: r.deptName ?? '',
                      value: r.deptId ?? '',
                    },
                  ]
                : undefined;
            return (
              <DeptSelectCell<PaymentRequestForm>
                key={`dept_${r.rowKey}`}
                control={control}
                inputId={`deptId${index}`}
                name={`invoiceTransactions.${index}.deptId`}
                rules={deptIdRule(index)}
                selectProps={baseSelectProps}
                defaultOptions={defaultOption}
                index={index}
                row={r}
              />
            );
          },
          resizable: true,
          width: 220,
        } satisfies Column,
        {
          title: (
            <RequiredHeader>
              {t('invoice_transaction_expense_account')}
            </RequiredHeader>
          ),
          dataIndex: 'exItem',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            const defaultOption =
              r.exItemId && r.exItemName
                ? [
                    {
                      value: r.exItemId ?? '',
                      label: r.exItemName ?? '',
                    },
                  ]
                : undefined;
            return (
              <ExItemSelectCell<PaymentRequestForm>
                key={`${r.id}_${v}_${index}`}
                control={control}
                inputId={`exItem${index}`}
                name={`invoiceTransactions.${index}.exItemId`}
                rules={exItemIdRule(index)}
                deptId={r.deptId}
                onChangeAfter={() =>
                  exItemIdSupporter(index, hasSpecialException, tpmData)
                }
                selectProps={baseSelectProps}
                defaultOptions={defaultOption}
                drExciseIdName={`invoiceTransactions.${index}.drExciseId`}
                deptIdName={`invoiceTransactions.${index}.deptId`}
                row={r}
                index={index}
              />
            );
          },
          resizable: true,
          width: 220,
        } satisfies Column,
        canDisplayTransactionDetail
          ? ({
              title: <RequiredHeader>{unitFormName.label}</RequiredHeader>,
              dataIndex: unitFormName.name,
              render(v, r: InvoiceTransaction) {
                const index = getIndex(r);
                return (
                  <FormInputNumberCell<PaymentRequestForm>
                    key={`${r.id}_${index}_unit_${isTaxIncluded}}`}
                    control={control}
                    row={r}
                    index={index}
                    inputId={`${unitFormName.name}${index}`}
                    rules={
                      isTaxIncluded
                        ? taxIncludedUnitRule(index)
                        : unitRule(index)
                    }
                    name={`invoiceTransactions.${index}.${unitFormName.name}`}
                    onChangeAfter={() => unitSupporter(index)}
                    computeInputProps={getCurrencySuffixProps}
                    computeInputPropsParams={memorizedCurrencyArgs(r)}
                    format={FORMATS.UNIT_WITH_EMPTY}
                  />
                );
              },
              resizable: true,
              width: 120,
            } satisfies Column)
          : {},
        canDisplayTransactionDetail
          ? ({
              title: (
                <RequiredHeader>
                  {t('invoice_transaction_quantity')}
                </RequiredHeader>
              ),
              dataIndex: 'quantity',
              render(v, r: InvoiceTransaction) {
                const index = getIndex(r);
                return (
                  <FormInputNumberCell<PaymentRequestForm>
                    control={control}
                    row={r}
                    index={index}
                    inputId={`quantity${index}`}
                    name={`invoiceTransactions.${index}.quantity`}
                    rules={quantityRule(index)}
                    onChangeAfter={() => quantitySupporter(index)}
                  />
                );
              },
              resizable: true,
              width: 110,
            } satisfies Column)
          : {},
        {
          title: <RequiredHeader>{totalValueFormName.label}</RequiredHeader>,
          dataIndex: totalValueFormName.name,
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            return (
              <FormInputNumberCell<PaymentRequestForm>
                key={`${r.id}_${index}_totalValue_${isTaxIncluded}`}
                control={control}
                row={r}
                index={index}
                inputId={`${totalValueFormName.name}${index}`}
                rules={
                  isTaxIncluded
                    ? taxIncludedTotalValueRule(index)
                    : totalValueRule(index)
                }
                name={`invoiceTransactions.${index}.${totalValueFormName.name}`}
                onChangeAfter={() => totalValueSupporter(index)}
                computeInputProps={getCurrencySuffixProps}
                computeInputPropsParams={memorizedCurrencyArgs(r)}
                format={FORMATS.UNIT_WITH_EMPTY}
              />
            );
          },
          resizable: true,
          width: 120,
        } satisfies Column,
        canDisplayDrExcise
          ? ({
              title: t('invoice_transaction_dr_excise_id'),
              dataIndex: 'drExciseId',
              render(v: string, r: InvoiceTransaction) {
                const index = getIndex(r);
                const defaultOption =
                  r.drExciseId && r.drExciseName
                    ? [
                        {
                          value: r.drExciseId ?? '',
                          label: r.drExciseName ?? '',
                        },
                      ]
                    : undefined;
                return (
                  <ExciseSelectCell<PaymentRequestForm>
                    key={`excise_select_${r.rowKey}`}
                    control={control}
                    inputId={`drExciseId${index}`}
                    name={`invoiceTransactions.${index}.drExciseId`}
                    onChangeAfter={() => {
                      drExciseIdSupporter(
                        index,
                        undefined,
                        hasSpecialException,
                        tpmData
                      );
                    }}
                    selectProps={baseSelectProps}
                    defaultOptions={defaultOption}
                    index={index}
                    row={r}
                  />
                );
              },
              resizable: true,
              width: 152,
            } satisfies Column)
          : {},
        {
          title: isApproverRoleFromPath
            ? t('invoice_transaction_invoice_kind')
            : '',
          dataIndex: 'invoiceKind',
          render(v: string, r: InvoiceTransaction) {
            const index = getIndex(r);
            const isNotTarget = r.invoiceKind === InvoiceKind.not_target;
            return isApproverRoleFromPath ? (
              <InvoiceKindCell
                key={`${r.id}_${v}_${index}`}
                control={control}
                inputId={`invoiceKind${index}`}
                name={`invoiceTransactions.${index}.invoiceKind`}
                isNotTarget={
                  isInvoiceKindUnqualifiedSeventyFlag
                    ? isNotTarget
                    : getIsZeroPer(r.drExciseId)
                }
                selectProps={baseSelectProps}
              />
            ) : (
              <FormController
                control={control}
                name={`invoiceTransactions.${index}.invoiceKind`}
              >
                {(...rest) => <input {...rest} hidden />}
              </FormController>
            );
          },
          resizable: true,
          width: isApproverRoleFromPath ? 152 : 0,
        } satisfies Column,
        canDisplayDrExcise
          ? ({
              title: t('invoice_transaction_excise_value'),
              dataIndex: 'exciseValue',
              render(v, r: InvoiceTransaction) {
                const index = getIndex(r);
                return (
                  <ExciseValueCell<PaymentRequestForm>
                    key={`${r.id}_${r.drExciseId}_${r[unitFormName.name]}_${
                      r[totalValueFormName.name]
                    }_${r.currency}_${index}_${r.exItemId}`}
                    control={control}
                    row={r}
                    index={index}
                    inputId={`exciseValue${index}`}
                    name={`invoiceTransactions.${index}.exciseValue`}
                    currency={r.currency}
                    drExciseId={r.drExciseId}
                    onChangeAfter={() => exciseValueSupporter(index)}
                    format={FORMATS.UNIT_WITH_EMPTY}
                  />
                );
              },
              resizable: true,
              width: 80,
            } satisfies Column)
          : {},
        hasWithholdingIncomeTax
          ? ({
              title: t('invoice_transaction_has_withholding_income_tax'),
              dataIndex: 'hasWithholdingIncomeTax',
              render(v, r: InvoiceTransaction) {
                const index = getIndex(r);
                const { disabled } = getForeignCurrencyDisableProps({
                  paymentMethod: selectedPayee?.payment_method,
                  currency: r?.currency,
                });
                return (
                  <HasWithHoldingIncomeTaxCheckBox
                    onChangeAfter={() => withholdingIncomeTaxSupporter(index)}
                    key={`${r.id}_${v}_${index}`}
                    control={control}
                    inputId={`hasWithholdingIncomeTax${index}`}
                    name={`invoiceTransactions.${index}.hasWithholdingIncomeTax`}
                    label={t(
                      'invoice_transaction_withholding_income_tax_label'
                    )}
                    rules={hasWithholdingIncomeTaxRule(index)}
                    disabled={disabled}
                  />
                );
              },
              resizable: true,
              width: 80,
            } satisfies Column)
          : {},
        hasWithholdingIncomeTax
          ? ({
              title: t('invoice_transaction_withholding_income_tax_amount'),
              dataIndex: 'withholdingIncomeTax',
              render(v, r: InvoiceTransaction) {
                const index = getIndex(r);
                const invoiceTransactions = getValues('invoiceTransactions');
                const updatedTransaction = invoiceTransactions[index];
                const { disabled: foreignCurrencyDisable } =
                  getForeignCurrencyDisableProps({
                    paymentMethod: selectedPayee?.payment_method,
                    currency: r?.currency,
                  });

                return (
                  <FormInputNumberCell<PaymentRequestForm>
                    key={`${r.id}_${r.exItemId}_${r.totalValue}_${r.taxIncludedTotalValue}_${r.hasWithholdingIncomeTax}_${r.currency}_${index}`}
                    control={control}
                    row={r}
                    index={index}
                    inputId={`withholdingIncomeTax${index}`}
                    name={`invoiceTransactions.${index}.withholdingIncomeTax`}
                    rules={withholdingIncomeTaxRule(index)}
                    format={FORMATS.UNIT_WITH_EMPTY}
                    computeInputPropsParams={memorizedWithholdingTaxArgs(
                      updatedTransaction?.hasWithholdingIncomeTax,
                      foreignCurrencyDisable
                    )}
                    computeInputProps={getWithholdingTaxInputProps}
                  />
                );
              },
              resizable: true,
              width: 120,
            } satisfies Column)
          : {},
        canDisplayTransactionDetail
          ? ({
              title: t('invoice_transaction_memo'),
              dataIndex: 'memo',
              render(v, r: InvoiceTransaction) {
                const index = getIndex(r);
                return (
                  <FormInputCell<PaymentRequestForm>
                    control={control}
                    row={r}
                    index={index}
                    inputId={`memo${index}`}
                    name={`invoiceTransactions.${index}.memo`}
                    rules={memoRule(index)}
                  />
                );
              },
              resizable: true,
              width: 152,
            } satisfies Column)
          : {},
        {
          title: t('invoice_transaction_project'),
          dataIndex: 'projectId',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            const projectId = r.projectId;
            const projectName = r.projectName;
            const defaultOption =
              projectId && projectName
                ? [
                    {
                      value: projectId ?? '',
                      label: projectName ?? '',
                    },
                  ]
                : undefined;

            return (
              <ProjectSelectCell<PaymentRequestForm>
                key={`project_${r.rowKey}`}
                control={control}
                inputId={`projectId${index}`}
                name={`invoiceTransactions.${index}.projectId`}
                rules={projectIdRule(index)}
                selectProps={baseSelectProps}
                defaultOptions={defaultOption}
                index={index}
                row={r}
              />
            );
          },
          resizable: true,
          width: 220,
        } satisfies Column,
        {
          title: (
            <RequiredHeader>
              {t('invoice_transaction_credit_account')}
            </RequiredHeader>
          ),
          dataIndex: 'crItemId',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            const defaultOption =
              r.crItemId && r.crItemName
                ? [
                    {
                      value: r.crItemId ?? '',
                      label: r.crItemName ?? '',
                    },
                  ]
                : undefined;
            return (
              <CrItemsSelectCell<PaymentRequestForm>
                key={`cr_items_${r.rowKey}`}
                control={control}
                inputId={`crItemId${index}`}
                name={`invoiceTransactions.${index}.crItemId`}
                rules={crItemIdRule(index)}
                selectProps={baseSelectProps}
                defaultOptions={defaultOption}
                clearCrSubItemName={`invoiceTransactions.${index}.crSubItemId`}
                index={index}
                row={r}
              />
            );
          },
          resizable: true,
          width: 150,
        } satisfies Column,
        {
          title: t('invoice_transaction_credit_sub_account'),
          dataIndex: 'crSubItemId',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            const defaultOption =
              r.crSubItemId && r.crSubItemName
                ? [
                    {
                      value: r.crSubItemId ?? '',
                      label: r.crSubItemName ?? '',
                    },
                  ]
                : undefined;
            return (
              <CrSubItemsSelectCell<PaymentRequestForm>
                key={`cr_sub_items_${r.rowKey}`}
                control={control}
                inputId={`crSubItemId${index}`}
                name={`invoiceTransactions.${index}.crSubItemId`}
                crItemName={`invoiceTransactions.${index}.crItemId`}
                selectProps={baseSelectProps}
                defaultOptions={defaultOption}
                index={index}
                row={r}
              />
            );
          },
          resizable: true,
          width: 150,
        } satisfies Column,
        {
          title: t('invoice_transaction_currency'),
          dataIndex: 'currency',
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            const defaultOption = r.currency
              ? [
                  {
                    value: r.currency ?? '',
                    label: r.currency ?? '',
                  },
                ]
              : undefined;

            return (
              <CurrencySelectCell<PaymentRequestForm>
                key={`currency_${r.rowKey}`}
                control={control}
                inputId={`currency${index}`}
                name={`invoiceTransactions.${index}.currency`}
                rules={currencyRule(index)}
                selectProps={getCurrencySelectProps(r?.currency)}
                defaultOptions={defaultOption}
                onChangeAfter={() => {
                  triggerRateValueSupporter(r);
                  currencyValueSupporter(index);
                }}
                index={index}
                row={r}
              />
            );
          },
          resizable: true,
          width: 120,
        } satisfies Column,
        {
          title: t('invoice_transaction_jpyRate'),
          render(v, r: InvoiceTransaction) {
            const index = getIndex(r);
            return loading[index] ? (
              <Loading size='small' />
            ) : (
              <RateInputCell<PaymentRequestForm>
                control={control}
                row={r}
                index={index}
                inputId={`jpyRate${index}`}
                name={`invoiceTransactions.${index}.jpyRate`}
                inputProps={getRateInputProps(r?.jpyRate)}
              />
            );
          },
          resizable: true,
          width: 128,
        } satisfies Column,
      ].filter((item: Column | {}): item is Column => 'title' in item),
    [
      t,
      canDisplayTransactionDetail,
      unitFormName.label,
      unitFormName.name,
      totalValueFormName.label,
      totalValueFormName.name,
      canDisplayDrExcise,
      isApproverRoleFromPath,
      hasWithholdingIncomeTax,
      getIndex,
      control,
      dealDateRule,
      containerRef,
      triggerRateValueSupporter,
      dealDateSupporter,
      hasSpecialException,
      tpmData,
      nameRule,
      deptIdRule,
      baseSelectProps,
      exItemIdRule,
      exItemIdSupporter,
      isTaxIncluded,
      taxIncludedUnitRule,
      unitRule,
      getCurrencySuffixProps,
      memorizedCurrencyArgs,
      unitSupporter,
      quantityRule,
      quantitySupporter,
      taxIncludedTotalValueRule,
      totalValueRule,
      totalValueSupporter,
      drExciseIdSupporter,
      getIsZeroPer,
      isInvoiceKindUnqualifiedSeventyFlag,
      exciseValueSupporter,
      selectedPayee?.payment_method,
      hasWithholdingIncomeTaxRule,
      withholdingIncomeTaxSupporter,
      getValues,
      withholdingIncomeTaxRule,
      memorizedWithholdingTaxArgs,
      getWithholdingTaxInputProps,
      memoRule,
      projectIdRule,
      crItemIdRule,
      currencyRule,
      getCurrencySelectProps,
      currencyValueSupporter,
      loading,
      getRateInputProps,
    ]
  );

  const columns = useMemo(() => {
    if (dynamicColumns.length > 0) {
      return [...fixedLeftColumns, ...dynamicColumns];
    }
    return [...fixedLeftColumns, ...defaultDataColumns];
  }, [dynamicColumns, fixedLeftColumns, defaultDataColumns]);

  return columns;
};
